import { useLiveQuery } from '@electric-sql/pglite-react';
import {
  ageProblem,
  birthDateProblem,
  emailProblem,
  nameProblem,
  phoneDigits,
  phoneProblem,
  SALUTATIONS,
  type Sex,
} from '@quickreport/domain';
import { Badge, Button, Field, Input, Modal, Select } from '@quickreport/ui';
import {
  ageFor,
  PatientError,
  type PatientFilters,
  type PatientListItem,
  type PatientSort,
  patientCountQuery,
  patientListQuery,
  savePatient,
} from '@quickreport/workspace';
import { Link, useNavigate } from '@tanstack/react-router';
import { FilePlus2, FileText, Pencil, Search } from 'lucide-react';
import { useDeferredValue, useEffect, useState } from 'react';
import { DEFAULT_PAGE, Pagination } from '../../components/Pagination';
import { useWorkspace } from '../../lib/workspace';

const when = (iso: string) =>
  new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

export function PatientsPage() {
  const ws = useWorkspace();
  const navigate = useNavigate();
  const open = (id: string) =>
    void navigate({ to: '/patients/$patientId', params: { patientId: id } });
  const [q, setQ] = useState('');
  const dq = useDeferredValue(q);
  const [sort, setSort] = useState<PatientSort>('name');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [sex, setSex] = useState<'' | Sex>('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE);
  const [editing, setEditing] = useState<PatientListItem | null>(null);

  const filters: PatientFilters = { q: dq, sort, order, ...(sex ? { sex } : {}) };
  const list = patientListQuery(ws.orgId, { ...filters, page, pageSize });
  const count = patientCountQuery(ws.orgId, filters);
  const res = useLiveQuery<PatientListItem>(list.sql, list.params);
  const totalRes = useLiveQuery<{ n: number }>(count.sql, count.params);
  const total = totalRes?.rows[0]?.n ?? 0;
  const rows = res?.rows ?? [];
  const canEdit = ws.can('register_visit');

  // biome-ignore lint/correctness/useExhaustiveDependencies: a new search or filter starts again from page 1
  useEffect(() => setPage(1), [dq, sort, order, sex, pageSize]);
  // If the list gets shorter (a filter, a deletion), stay within the pages that still exist. Using
  // the latest page number here keeps this from undoing a reset to page 1.
  useEffect(() => {
    const last = Math.max(1, Math.ceil(total / pageSize));
    setPage((p) => Math.min(p, last));
  }, [total, pageSize]);

  return (
    <div className="mx-auto max-w-6xl p-4 sm:p-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">Patients</h1>
          <p className="mt-1 text-slate">
            Everyone who has had a report made. Starting a report for a returning patient fills in
            their details.
          </p>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-2 max-md:[&_select]:h-10 max-md:[&_select]:w-full md:mt-6 md:flex md:flex-wrap md:items-end md:gap-3">
        <div className="relative col-span-3 min-w-0 md:min-w-64 md:flex-1">
          <Search size={16} className="pointer-events-none absolute top-2 left-2.5 text-slate" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, mobile number or patient ID"
            aria-label="Search patients"
            className="h-10 pl-8 md:h-9"
          />
        </div>
        <Field label="Sort by">
          <Select
            value={sort}
            onChange={(e) => {
              const v = e.target.value as PatientSort;
              setSort(v);
              setOrder(v === 'name' ? 'asc' : 'desc');
            }}
            className="h-9 w-44"
            aria-label="Sort by"
          >
            <option value="name">Name</option>
            <option value="added">Recently added</option>
            <option value="updated">Recently changed</option>
          </Select>
        </Field>
        <Field label="Order">
          <Select
            value={order}
            onChange={(e) => setOrder(e.target.value as 'asc' | 'desc')}
            className="h-9 w-36"
            aria-label="Order"
          >
            <option value="asc">{sort === 'name' ? 'A to Z' : 'Oldest first'}</option>
            <option value="desc">{sort === 'name' ? 'Z to A' : 'Newest first'}</option>
          </Select>
        </Field>
        <Field label="Sex">
          <Select
            value={sex}
            onChange={(e) => setSex(e.target.value as '' | Sex)}
            className="h-9 w-32"
            aria-label="Sex"
          >
            <option value="">Any</option>
            <option value="M">Male</option>
            <option value="F">Female</option>
            <option value="O">Other</option>
          </Select>
        </Field>
      </div>

      <ul className="mt-3 grid gap-2 md:hidden" aria-label="Patients">
        {rows.map((p) => {
          const age = ageFor(p).ageText;
          const sexName =
            p.sex === 'M' ? 'Male' : p.sex === 'F' ? 'Female' : p.sex === 'O' ? 'Other' : '';
          return (
            // biome-ignore lint/a11y/useKeyWithClickEvents: keyboard users open it with the name link inside
            <li
              key={p.id}
              onClick={() => open(p.id)}
              className="cursor-pointer rounded-xl border border-line bg-paper p-3 active:bg-brand-soft/50"
            >
              <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1">
                  <Link
                    to="/patients/$patientId"
                    params={{ patientId: p.id }}
                    onClick={(e) => e.stopPropagation()}
                    className="block leading-snug font-medium text-ink"
                  >
                    {[p.salutation ? `${p.salutation}.` : '', p.name].filter(Boolean).join(' ')}
                  </Link>
                  <div className="mt-0.5 text-[13px] text-slate">
                    {[age, sexName].filter(Boolean).join(' · ') || 'Age not given'}
                  </div>
                </div>
                <Badge tone={p.report_count ? 'brand' : 'neutral'}>
                  {p.report_count} report{p.report_count === 1 ? '' : 's'}
                </Badge>
              </div>
              <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-0.5 text-[12px] text-slate">
                {p.phone ? <span>{p.phone}</span> : null}
                {p.external_id ? <span>ID {p.external_id}</span> : null}
                <span>Added {when(p.created_at)}</span>
              </div>
              {/* biome-ignore lint/a11y/useKeyWithClickEvents: the buttons inside are the keyboard controls */}
              {/* biome-ignore lint/a11y/noStaticElementInteractions: only keeps these buttons from opening the card */}
              <div className="mt-2.5 flex gap-2" onClick={(e) => e.stopPropagation()}>
                {canEdit ? (
                  <Link
                    to="/reports/new"
                    search={{ patient: p.id }}
                    className="inline-flex h-10 flex-1 items-center justify-center gap-1.5 rounded-lg bg-navy text-[13px] font-medium text-white"
                  >
                    <FilePlus2 size={15} /> New report
                  </Link>
                ) : null}
                <Link
                  to="/reports"
                  search={{ q: p.phone ?? p.name }}
                  className="inline-flex h-10 flex-1 items-center justify-center gap-1.5 rounded-lg border border-line text-[13px] font-medium text-navy"
                >
                  <FileText size={15} /> Reports
                </Link>
                {canEdit ? (
                  <button
                    type="button"
                    aria-label={`Edit ${p.name}`}
                    onClick={() => setEditing(p)}
                    className="grid h-10 w-10 shrink-0 place-items-center rounded-lg border border-line text-navy"
                  >
                    <Pencil size={16} />
                  </button>
                ) : null}
              </div>
            </li>
          );
        })}
        {res && rows.length === 0 ? (
          <li className="rounded-xl border border-dashed border-line bg-paper px-4 py-10 text-center text-slate">
            {dq || sex
              ? 'No patient matches. Try another name, number or ID.'
              : 'No patients yet. They appear here once you make a report.'}
          </li>
        ) : null}
      </ul>

      <div className="mt-4 hidden overflow-hidden rounded-lg border border-line bg-paper md:block">
        <table className="w-full text-left">
          <thead className="bg-canvas text-[12px] tracking-wide text-slate uppercase">
            <tr>
              <th className="px-4 py-2 font-medium">Name</th>
              <th className="px-4 py-2 font-medium">Age / Sex</th>
              <th className="px-4 py-2 font-medium">Mobile</th>
              <th className="px-4 py-2 font-medium">Patient ID</th>
              <th className="px-4 py-2 text-right font-medium">Reports</th>
              <th className="px-4 py-2 font-medium">Added</th>
              <th className="w-44 px-2 py-2">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => {
              const age = ageFor(p).ageText;
              return (
                // biome-ignore lint/a11y/useKeyWithClickEvents: keyboard users open it with the name link inside
                <tr
                  key={p.id}
                  onClick={() => open(p.id)}
                  className="cursor-pointer border-t border-line hover:bg-brand-soft/50"
                >
                  <td className="px-4 py-2.5 font-medium">
                    <Link
                      to="/patients/$patientId"
                      params={{ patientId: p.id }}
                      onClick={(e) => e.stopPropagation()}
                      className="text-ink hover:text-brand"
                    >
                      {[p.salutation ? `${p.salutation}.` : '', p.name].filter(Boolean).join(' ')}
                    </Link>
                  </td>
                  <td className="px-4 py-2.5 text-slate">
                    {[
                      age,
                      p.sex === 'M'
                        ? 'Male'
                        : p.sex === 'F'
                          ? 'Female'
                          : p.sex === 'O'
                            ? 'Other'
                            : '',
                    ]
                      .filter(Boolean)
                      .join(' · ') || '—'}
                  </td>
                  <td className="px-4 py-2.5 text-slate">{p.phone ?? '—'}</td>
                  <td className="px-4 py-2.5 text-slate">{p.external_id ?? '—'}</td>
                  <td className="px-4 py-2.5 text-right">
                    <Badge tone={p.report_count ? 'brand' : 'neutral'}>{p.report_count}</Badge>
                  </td>
                  <td className="px-4 py-2.5 text-[13px] text-slate">{when(p.created_at)}</td>
                  {/* biome-ignore lint/a11y/useKeyWithClickEvents: the buttons inside are the keyboard controls */}
                  {/* biome-ignore lint/a11y/noStaticElementInteractions: only keeps these buttons from opening the row */}
                  <td className="px-2 py-2.5 text-right" onClick={(e) => e.stopPropagation()}>
                    <span className="inline-flex gap-1">
                      {canEdit ? (
                        <Link
                          to="/reports/new"
                          search={{ patient: p.id }}
                          title="Start a new report for this patient"
                          aria-label={`New report for ${p.name}`}
                          className="grid h-8 w-8 place-items-center rounded-lg text-navy hover:bg-brand-soft"
                        >
                          <FilePlus2 size={16} />
                        </Link>
                      ) : null}
                      <Link
                        to="/reports"
                        search={{ q: p.phone ?? p.name }}
                        title="See this patient's reports"
                        aria-label={`Reports of ${p.name}`}
                        className="grid h-8 w-8 place-items-center rounded-lg text-navy hover:bg-brand-soft"
                      >
                        <FileText size={16} />
                      </Link>
                      {canEdit ? (
                        <button
                          type="button"
                          title="Edit details"
                          aria-label={`Edit ${p.name}`}
                          onClick={() => setEditing(p)}
                          className="grid h-8 w-8 place-items-center rounded-lg text-navy hover:bg-brand-soft"
                        >
                          <Pencil size={15} />
                        </button>
                      ) : null}
                    </span>
                  </td>
                </tr>
              );
            })}
            {res && rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-12 text-center text-slate">
                  {dq || sex
                    ? 'No patient matches. Try another name, number or ID.'
                    : 'No patients yet. They appear here once you make a report.'}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
      <Pagination
        page={page}
        pageSize={pageSize}
        total={total}
        onPage={setPage}
        onPageSize={setPageSize}
        noun="patients"
      />

      {editing ? (
        <EditPatient
          patient={editing}
          onClose={() => setEditing(null)}
          onSaved={() => void ws.engine.syncNow()}
        />
      ) : null}
    </div>
  );
}

export function EditPatient({
  patient,
  onClose,
  onSaved,
}: {
  patient: PatientListItem;
  onClose: () => void;
  onSaved: () => void;
}) {
  const ws = useWorkspace();
  const a = ageFor(patient);
  const m = /^(\d+)\s*([YMD])$/.exec(a.ageText);
  const [f, setF] = useState({
    salutation: patient.salutation ?? '',
    name: patient.name,
    sex: (patient.sex ?? '') as Sex | '',
    ageAmount: patient.dob ? '' : (m?.[1] ?? ''),
    ageUnit: (m?.[2] ?? 'Y') as 'Y' | 'M' | 'D',
    dob: patient.dob ?? '',
    useDob: Boolean(patient.dob),
    phone: patient.phone ?? '',
    email: patient.email ?? '',
    address: patient.address ?? '',
    externalId: patient.external_id ?? '',
  });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = (p: Partial<typeof f>) => setF((x) => ({ ...x, ...p }));
  // Problems are said beside the field once it has been left, or after pressing Save.
  const [tried, setTried] = useState(false);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const touch = (k: string) => setTouched((t) => (t[k] ? t : { ...t, [k]: true }));
  const problems: Record<string, string | null> = {
    name: nameProblem(f.name, 'the patient’s name'),
    age: f.useDob ? birthDateProblem(f.dob) : ageProblem(f.ageAmount, f.ageUnit),
    phone: phoneProblem(f.phone),
    email: emailProblem(f.email),
  };
  const shown = (k: string): string | null =>
    k === 'phone' && phoneDigits(f.phone).length > 10
      ? (problems.phone ?? null)
      : tried || touched[k]
        ? (problems[k] ?? null)
        : null;

  async function save() {
    if (Object.values(problems).some(Boolean)) {
      setTried(true);
      window.setTimeout(
        () =>
          document
            .querySelector<HTMLElement>(
              '[role="dialog"] label:has([role="alert"]) :is(input,select), [role="dialog"] [aria-invalid="true"]',
            )
            ?.focus(),
        60,
      );
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await ws.db.transaction((tx) =>
        savePatient(tx, ws.orgId, {
          id: patient.id,
          ...(f.salutation ? { salutation: f.salutation } : {}),
          name: f.name,
          ...(f.sex ? { sex: f.sex } : {}),
          ...(f.useDob && f.dob ? { dob: f.dob } : {}),
          ...(!f.useDob && f.ageAmount
            ? { age: { amount: Number(f.ageAmount), unit: f.ageUnit } }
            : {}),
          phone: f.phone,
          email: f.email,
          address: f.address,
          externalId: f.externalId,
        }),
      );
      onSaved();
      onClose();
    } catch (err) {
      setError(
        err instanceof PatientError || err instanceof Error ? err.message : 'Could not save.',
      );
      setBusy(false);
    }
  }

  return (
    <Modal wide title="Edit patient" label="Edit patient" onClose={onClose}>
      <p className="text-[13px] text-slate">
        Changes apply to new reports. Reports already made keep the details they were printed with.
      </p>
      <div className="grid min-h-0 flex-1 gap-3 overflow-auto p-0.5 md:grid-cols-2">
        <div className="grid grid-cols-[6rem_1fr] items-start gap-3 md:col-span-2">
          <Field label="Title">
            <Select value={f.salutation} onChange={(e) => set({ salutation: e.target.value })}>
              <option value="" />
              {SALUTATIONS.map((s) => (
                <option key={s}>{s}</option>
              ))}
            </Select>
          </Field>
          <Field label="Name" error={shown('name')}>
            <Input
              value={f.name}
              onChange={(e) => set({ name: e.target.value })}
              onBlur={() => touch('name')}
            />
          </Field>
        </div>
        <Field label="Sex">
          <Select value={f.sex} onChange={(e) => set({ sex: e.target.value as Sex | '' })}>
            <option value="" />
            <option value="M">Male</option>
            <option value="F">Female</option>
            <option value="O">Other</option>
          </Select>
        </Field>
        <div className="grid min-w-0 gap-1">
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-[12px] font-medium text-slate">
              {f.useDob ? 'Date of birth' : 'Age'}
            </span>
            <button
              type="button"
              className="text-[12px] text-brand hover:underline"
              onClick={() => set({ useDob: !f.useDob })}
            >
              {f.useDob ? 'Enter age instead' : 'Use date of birth'}
            </button>
          </div>
          {f.useDob ? (
            <Input
              type="date"
              value={f.dob}
              onChange={(e) => set({ dob: e.target.value })}
              onBlur={() => touch('age')}
              aria-invalid={shown('age') ? true : undefined}
              className={shown('age') ? 'border-flag' : ''}
              aria-label="Date of birth"
            />
          ) : (
            <div className="flex gap-2">
              <Input
                inputMode="numeric"
                value={f.ageAmount}
                onChange={(e) => set({ ageAmount: e.target.value.replace(/\D/g, '') })}
                onBlur={() => touch('age')}
                aria-invalid={shown('age') ? true : undefined}
                className={shown('age') ? 'w-20 border-flag' : 'w-20'}
                aria-label="Age"
              />
              <Select
                value={f.ageUnit}
                onChange={(e) => set({ ageUnit: e.target.value as 'Y' | 'M' | 'D' })}
                className="w-28"
                aria-label="Age unit"
              >
                <option value="Y">years</option>
                <option value="M">months</option>
                <option value="D">days</option>
              </Select>
            </div>
          )}
          {shown('age') ? (
            <span role="alert" className="text-[12px] font-medium text-flag">
              {shown('age')}
            </span>
          ) : null}
        </div>
        <Field label="Mobile" hint="10 digits" error={shown('phone')}>
          <Input
            value={f.phone}
            onChange={(e) => set({ phone: e.target.value })}
            onBlur={() => touch('phone')}
            inputMode="tel"
          />
        </Field>
        <Field label="Email" error={shown('email')}>
          <Input
            value={f.email}
            onChange={(e) => set({ email: e.target.value })}
            onBlur={() => touch('email')}
            inputMode="email"
          />
        </Field>
        <Field label="Address" className="md:col-span-2">
          <Input value={f.address} onChange={(e) => set({ address: e.target.value })} />
        </Field>
        <Field label="Patient ID">
          <Input value={f.externalId} onChange={(e) => set({ externalId: e.target.value })} />
        </Field>
      </div>
      {error ? (
        <p role="alert" className="text-[13px] text-flag">
          {error}
        </p>
      ) : null}
      <div className="flex justify-end gap-2">
        <Button onClick={onClose}>Cancel</Button>
        <Button tone="primary" onClick={() => void save()} disabled={busy}>
          Save
        </Button>
      </div>
    </Modal>
  );
}
